import { Dropdown, Option } from "@fluentui/react-components";
import { FinOpsTrackingViewTypeEnum, IFinOpsBudgetAllocationCombined } from "../../../models/FinOpsBudgets";
import { LogComponent, LogElement } from "../../../models/LogModel";
import React, { useMemo, useState } from "react";
import {
  buildChartSeries,
  getAllMonthsBetween,
  getCurrentMonth,
  getFiscalYearRange,
  getPreviousMonth
} from "../../../utils/BudgetUtils";
import { useGetFyBaseline, useGetMonthlyBudgetAllocation } from "../../../hooks/useFinOpsBudget";

import { BudgetLineChart } from "../Common/BudgetLineChart";
import { BudgetOverviewCards } from "../Common/BudgetOverviewCard";
import CategoryTypeHeatmap from "../Common/CategoryHeatmap";
import FinOpsCostTable from "../Common/DetailTable";
import ServiceDrillDownLayout from "./ServiceDrillDownLayout";
import SummaryTable from "../Common/SummaryTable";
import { currencyFormatter } from "../../../utils/currency";
import styles from "./BudgetTracking.less";

interface ServiceLayoutProps {
  service: string;
  data: IFinOpsBudgetAllocationCombined[];
  isLoadingBudgetData: boolean;
  currentFiscalYear: string;
}


const ServiceLayout: React.FC<ServiceLayoutProps> = ({ service, data, isLoadingBudgetData, currentFiscalYear }) => {
  const fiscalYears = Array.from({ length: parseInt(currentFiscalYear.slice(2)) - 26 + 1 }, (_, i) => `FY${26 + i}`);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(currentFiscalYear);

  const { start: fiscalStart, end: fiscalEnd } = React.useMemo(() => getFiscalYearRange(selectedFiscalYear), []);
  const allMonths = getAllMonthsBetween(fiscalStart, fiscalEnd);
  const currentMonth = getCurrentMonth();
  const lastMonth = getPreviousMonth();
  const previousFiscalYear = `FY${parseInt(selectedFiscalYear.slice(2)) - 1}`;
  const { start: prevFiscalStart, end: prevFiscalEnd } = React.useMemo(() => getFiscalYearRange(previousFiscalYear), []);
  const [selectedDrilldown, setSelectedDrilldown] = useState<{ category: string; type: string } | null>(null);
  const { data: goldenBaseline = [], isLoading, error } = useGetFyBaseline({ fiscalYear: previousFiscalYear });
  const filteredBaseline = useMemo(() => {
    return goldenBaseline.filter(d => {
      if (!d.serviceName) {
        return false;
      }
      
      return d.serviceName === service;
    });
  }, [goldenBaseline, service]);  

  const { data: lastFyServiceBudgetData = [], isLoading: isloadingPrev, error: errorPrev } = useGetMonthlyBudgetAllocation({ fiscalYear: previousFiscalYear });
  const filteredLastFyData = useMemo(() => {
    return lastFyServiceBudgetData.filter(d => {
      if (!d.serviceName) {
        return false;
      }
      
      return d.serviceName === service;
    });
  }, [lastFyServiceBudgetData, service]);  

  const series = buildChartSeries(data, currentMonth);

  const handleBack = () => {
    setSelectedDrilldown(null);
  };

  if (selectedDrilldown) {
    return (
      <div className={styles.serviceLayout}>
        <ServiceDrillDownLayout
          service={`${selectedDrilldown.category}:${selectedDrilldown.type}`}
          data={data.filter(d => d.category === selectedDrilldown.category && d.type === selectedDrilldown.type)}
          baselineData={filteredBaseline.filter(d => d.category === selectedDrilldown.category && d.type === selectedDrilldown.type)}
          previousFyData={filteredLastFyData.filter(d => d.category === selectedDrilldown.category && d.type === selectedDrilldown.type)}
          isLoadingBudgetData={isLoadingBudgetData}
          currentFiscalYear={currentFiscalYear}
          onBack={handleBack}
        />
      </div>
    );
  } else {
    return (
      <div className={styles.serviceLayout}>
        <h1 className={styles.layoutTitle}>{service}</h1>
        <p className={styles.description}>
          This page provides service-level budget tracking, allowing you to view detailed budget and cost data per service, adjust monthly budget allocations, and explore category and type-level breakdowns via the category heatmap.
        </p>

        <div className={styles.dropdownContainer}>
          <label id={"Select Fiscal Year"}>FiscalYear</label>
          <Dropdown
            aria-labelledby={"Select Fiscal Year"}
            placeholder="Choose a fiscal year"
            defaultValue={selectedFiscalYear}
            value={selectedFiscalYear}
            onOptionSelect={(_, data) => {
              if (data.optionValue) {
                setSelectedFiscalYear(data.optionValue);
              }
            }}>
            {fiscalYears.map((option) => (
              <Option key={option}>
                {option}
              </Option>
            ))}
          </Dropdown>
        </div>

        <BudgetOverviewCards
          currentFiscalYear={selectedFiscalYear}
          previousFiscalYear={previousFiscalYear}
          currentMonth={currentMonth}
          data={data}
          baselineData={filteredBaseline}
          isLoadingBudgetData={isLoadingBudgetData}
          currencyFormatter={currencyFormatter}
          viewType="overview"
        />

        {/* Line Chart */}
        <div className={styles.lineChartAndSummary}>
          <BudgetLineChart
            title="Budget vs Actuals"
            isLoading={isLoadingBudgetData}
            series={series}
            logProps={{
              logComponent: LogComponent.BudgetTracking,
              logElement: LogElement.BudgetTrackingServiceLayout,
              customProperties: { service }
            }}
            fillMissingAbnormalData
            anomalies={[]}
            suffix=""
            hideLegend={true}
          />

          <SummaryTable
            previousFiscalYear={previousFiscalYear}
            currentFiscalYear={selectedFiscalYear}
            currentMonth={currentMonth}
            data={data}
            baselineData={filteredBaseline}
            isLoadingBudgetData={isLoadingBudgetData}
            viewLevel="service"
          />
        </div>

        {/* Detail Table */}
        <FinOpsCostTable
          data={data}
          previousFyData={filteredLastFyData}
          viewType={FinOpsTrackingViewTypeEnum.Service}
          isloading={isLoadingBudgetData}
          IsDrilldownView={false}
          currentFiscalYear={currentFiscalYear}
        />

        <CategoryTypeHeatmap data={data} isloadingBudgetData={isLoadingBudgetData} onDrilldownSelect={(selection) => setSelectedDrilldown(selection)}/>
      </div>
  );}
};

export default ServiceLayout;
